import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  CheckCircle as ActiveIcon,
  CheckCircle,
  Cancel as InactiveIcon,
  Pending as PendingIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";

// Styled container for the status tag
const StatusContainer = styled(Box)(({ theme, variant, status }) => {
  const getStatusConfig = (status, theme) => {
    const configs = {
      active: {
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(56, 142, 60, 0.15) 100%)"
            : "linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(200, 230, 201, 0.8) 100%)",
        color: theme.palette.mode === "dark" ? "#81c784" : "#2e7d32",
        border:
          theme.palette.mode === "dark"
            ? "rgba(76, 175, 80, 0.3)"
            : "rgba(76, 175, 80, 0.2)",
        shadow:
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(76, 175, 80, 0.15)"
            : "0 2px 8px rgba(76, 175, 80, 0.1)",
      },
      inactive: {
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(244, 67, 54, 0.2) 0%, rgba(211, 47, 47, 0.15) 100%)"
            : "linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(255, 205, 210, 0.8) 100%)",
        color: theme.palette.mode === "dark" ? "#e57373" : "#c62828",
        border:
          theme.palette.mode === "dark"
            ? "rgba(244, 67, 54, 0.3)"
            : "rgba(244, 67, 54, 0.2)",
        shadow:
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(244, 67, 54, 0.15)"
            : "0 2px 8px rgba(244, 67, 54, 0.1)",
      },
      pending: {
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(245, 124, 0, 0.15) 100%)"
            : "linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 224, 178, 0.8) 100%)",
        color: theme.palette.mode === "dark" ? "#ffb74d" : "#e65100",
        border:
          theme.palette.mode === "dark"
            ? "rgba(255, 152, 0, 0.3)"
            : "rgba(255, 152, 0, 0.2)",
        shadow:
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(255, 152, 0, 0.15)"
            : "0 2px 8px rgba(255, 152, 0, 0.1)",
      },
      info: {
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.2
              )} 0%, ${alpha(theme.palette.primary.dark, 0.15)} 100%)`
            : `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 0%, ${alpha(theme.palette.primary.light, 0.3)} 100%)`,
        color:
          theme.palette.mode === "dark"
            ? theme.palette.primary.light
            : theme.palette.primary.dark,
        border:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.3)
            : alpha(theme.palette.primary.main, 0.2),
        shadow:
          theme.palette.mode === "dark"
            ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
            : `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
      },
      warning: {
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255, 193, 7, 0.2) 0%, rgba(255, 160, 0, 0.15) 100%)"
            : "linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 236, 179, 0.8) 100%)",
        color: theme.palette.mode === "dark" ? "#ffd54f" : "#f57c00",
        border:
          theme.palette.mode === "dark"
            ? "rgba(255, 193, 7, 0.3)"
            : "rgba(255, 193, 7, 0.2)",
        shadow:
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(255, 193, 7, 0.15)"
            : "0 2px 8px rgba(255, 193, 7, 0.1)",
      },
      error: {
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(244, 67, 54, 0.2) 0%, rgba(211, 47, 47, 0.15) 100%)"
            : "linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(255, 205, 210, 0.8) 100%)",
        color: theme.palette.mode === "dark" ? "#e57373" : "#c62828",
        border:
          theme.palette.mode === "dark"
            ? "rgba(244, 67, 54, 0.3)"
            : "rgba(244, 67, 54, 0.2)",
        shadow:
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(244, 67, 54, 0.15)"
            : "0 2px 8px rgba(244, 67, 54, 0.1)",
      },
      acknowledged: {
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(
                theme.palette.secondary.main,
                0.2
              )} 0%, ${alpha(theme.palette.secondary.dark, 0.15)} 100%)`
            : `linear-gradient(135deg, ${alpha(
                theme.palette.secondary.main,
                0.1
              )} 0%, ${alpha(theme.palette.secondary.light, 0.3)} 100%)`,
        color:
          theme.palette.mode === "dark"
            ? theme.palette.secondary.light
            : theme.palette.secondary.dark,
        border:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.secondary.main, 0.3)
            : alpha(theme.palette.secondary.main, 0.2),
        shadow:
          theme.palette.mode === "dark"
            ? `0 2px 8px ${alpha(theme.palette.secondary.main, 0.15)}`
            : `0 2px 8px ${alpha(theme.palette.secondary.main, 0.1)}`,
      },
    };

    return configs[status.toLowerCase()] || configs.info;
  };

  const config = getStatusConfig(status, theme);

  return {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding:
      variant === "large"
        ? theme.spacing(1, 1.5)
        : variant === "small"
        ? theme.spacing(0.25, 0.75)
        : theme.spacing(0.5, 1),
    borderRadius: variant === "large" ? 12 : variant === "small" ? 8 : 10,
    background: config.background,
    color: config.color,
    border: `1px solid ${config.border}`,
    boxShadow: config.shadow,
    fontFamily: theme.typography.fontFamily,
    fontWeight: variant === "large" ? 600 : 500,
    fontSize:
      variant === "large"
        ? "0.875rem"
        : variant === "small"
        ? "0.625rem"
        : "0.75rem",
    textTransform: "capitalize",
    transition: "all 0.2s ease-in-out",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    cursor: "default",
    userSelect: "none",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow:
        theme.palette.mode === "dark"
          ? `0 4px 12px ${alpha(config.color, 0.2)}`
          : `0 4px 12px ${alpha(config.color, 0.15)}`,
    },
  };
});

// Icon mapping for different statuses
const getStatusIcon = (status, size = "small") => {
  const iconSize = size === "large" ? 18 : size === "small" ? 12 : 14;

  const icons = {
    active: <ActiveIcon sx={{ fontSize: iconSize }} />,
    inactive: <InactiveIcon sx={{ fontSize: iconSize }} />,
    pending: <PendingIcon sx={{ fontSize: iconSize }} />,
    info: <InfoIcon sx={{ fontSize: iconSize }} />,
    warning: <WarningIcon sx={{ fontSize: iconSize }} />,
    error: <ErrorIcon sx={{ fontSize: iconSize }} />,
    acknowledged: <CheckCircle sx={{ fontSize: iconSize }} />,
    "needs more info": <InfoIcon sx={{ fontSize: iconSize }} />,
    needs_more_info: <InfoIcon sx={{ fontSize: iconSize }} />,
    schedule: <ScheduleIcon sx={{ fontSize: iconSize }} />,
  };

  return icons[status.toLowerCase()] || icons.info;
};

// Format label for display
const formatLabel = (status) => {
  if (typeof status !== "string") return "Unknown";

  // Handle snake_case and camelCase
  return status
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getTranslationKey = (status) => {
  const normalizedStatus = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  const translationMap = {
    active: "common.active",
    inactive: "common.inactive",
    pending: "common.pending",
    acknowledged: "common.acknowledged",
    warning: "common.warning",
    error: "common.error",
    info: "common.info",
    success: "common.success",
    contributed: "topics.contributed",
    open: "topics.notContributed",
    not_contributed: "topics.notContributed",
    needs_more_info: "statusTag.needsMoreInfo",
    schedule: "statusTag.schedule",
    scheduled: "statusTag.schedule",
  };

  return translationMap[normalizedStatus];
};

const StatusTag = ({
  status = "info",
  variant = "medium",
  showIcon = true,
  onClick,
  className,
  ...props
}) => {
  const { t } = useTranslation();

  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(status);
    }
  };

  const translatedLabel = getTranslationKey(status)
    ? t(getTranslationKey(status))
    : formatLabel(status);

  return (
    <StatusContainer
      status={status}
      variant={variant}
      onClick={handleClick}
      className={className}
      sx={{
        cursor: onClick ? "pointer" : "default",
        ...props.sx,
      }}
      {...props}
    >
      {showIcon && getStatusIcon(status, variant)}
      <Typography
        component="span"
        sx={{
          fontSize: "inherit",
          fontWeight: "inherit",
          fontFamily: "inherit",
          lineHeight: 1,
        }}
      >
        {translatedLabel}
      </Typography>
    </StatusContainer>
  );
};

StatusTag.propTypes = {
  status: PropTypes.oneOf([
    "active",
    "inactive",
    "pending",
    "info",
    "warning",
    "error",
    "acknowledged",
    "needs more info",
    "needs_more_info",
    "schedule",
  ]).isRequired,
  variant: PropTypes.oneOf(["small", "medium", "large"]),
  showIcon: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default StatusTag;
